"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session } from "@supabase/supabase-js";
import { usePathname, useRouter } from "next/navigation";
import * as Sentry from "@sentry/nextjs";
import { supabase } from "../../lib/supabase";
import { authFetch } from "../../lib/authFetch";
import { API_BASE } from "../../lib/api";
import { identify as analyticsIdentify, reset as analyticsReset } from "../../lib/analytics";

interface AuthContextType {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Supabase client is created without overrides, so `detectSessionInUrl`
    // defaults to true (@supabase/auth-js >= 2). On email-confirmation and
    // OAuth implicit-grant redirects, gotrue parses the `#access_token=...`
    // fragment, hydrates the session, and clears the hash itself
    // (GoTrueClient `window.location.hash = ''` after `_getSessionFromURL`;
    // PKCE uses `history.replaceState` to strip `?code=`). We do not need
    // to manually scrub the URL here -- keeping this note so future agents
    // don't re-investigate the "tokens persist in URL" claim.
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      // Identify the user to PostHog + Sentry as soon as we know who
      // they are. Only the Supabase UUID is sent -- never email or
      // display name. Per the privacy commitment in the plan.
      if (session?.user?.id) {
        analyticsIdentify(session.user.id);
        Sentry.setUser({ id: session.user.id });
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user?.id) {
        analyticsIdentify(session.user.id);
        Sentry.setUser({ id: session.user.id });
      } else {
        analyticsReset();
        Sentry.setUser(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signOut() {
    // Reset analytics + Sentry user context BEFORE supabase.signOut so
    // any in-flight signout-related events don't get attributed to
    // the next anonymous session.
    analyticsReset();
    Sentry.setUser(null);
    await supabase.auth.signOut();
  }

  return (
    <AuthContext.Provider value={{ session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function useRequireAuth() {
  const { session, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    if (loading) return;
    if (!session) {
      // Preserve the requested path so /login can route back after
      // sign-in. Restrict to relative paths starting with "/" and not
      // "//" to avoid being an open redirect.
      const safeNext =
        pathname && pathname.startsWith("/") && !pathname.startsWith("//")
          ? `?next=${encodeURIComponent(pathname)}`
          : "";
      router.push(`/login${safeNext}`);
      return;
    }
    // Check onboarding status on first protected-page load. The /welcome
    // page itself uses useAuth (not useRequireAuth) so this hook won't
    // fire there. Pages under /login, /signup, /terms, /privacy, etc.
    // don't call useRequireAuth, so they're unaffected.
    if (pathname === "/welcome") return;
    authFetch(`${API_BASE}/profile`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && !data.onboarded_at) {
          router.push("/welcome");
        }
      })
      .catch(() => {
        /* silent — onboarding check is non-critical; the user
           hits /welcome manually via a menu if the redirect fails. */
      });
  }, [session, loading, router, pathname]);

  return { session, loading };
}
